import { Checkbox } from 'antd';
import { observer } from 'mobx-react';
import {
    type DragEvent as ReactDragEvent,
    type MouseEvent as ReactMouseEvent,
    useEffect,
} from 'react';
import styled from 'styled-components';

import { CommentCount } from 'components/UI/CommentCount';
import type Ad from 'stores/Ad';
import { addChangeListener } from 'stores/DynamicProperties/changeListeners';
import type DynamicPropertiesManager from 'stores/DynamicProperties/Manager';
import type DynamicPropertyLabel from 'stores/DynamicProperties/Types/Label';
import { DynamicPropertyType } from 'types/DynamicPropertyType';
import { useForceUpdate } from 'utils/Hooks';

import type {
    AdListTableRowCallback,
    AdListTableRowCheckCallback,
} from './types';

const BGCOLOR_IDLE = '#ffffff';

// If currently viewing, no need to respond to hover; just use this color
const BGCOLOR_VIEWING = '#e1f3ff';

const BGCOLOR_SELECTED = '#f4f4f4';
const BGCOLOR_HOVER = '#f4f4f4';
const BGCOLOR_SELECTED_HOVER = '#e9e9e9'; // BGCOLOR_SELECTED x BGCOLOR_HOVER

interface ContainerProps {
    $isSelected?: boolean;
    $isViewing?: boolean;
    $isHidden?: boolean;
}

function bgIdleColor(p: ContainerProps) {
    if (p.$isViewing) return BGCOLOR_VIEWING;
    return p.$isSelected ? BGCOLOR_SELECTED : BGCOLOR_IDLE;
}

function bgHoverColor(p: ContainerProps) {
    if (p.$isViewing) return BGCOLOR_VIEWING;
    return p.$isSelected ? BGCOLOR_SELECTED_HOVER : BGCOLOR_HOVER;
}

// The transition applied to opacity is not just for aesthetics; it gives
// the browser a chance to capture the row for the drag image before it
// disappears. It's a convenient hack around having to defer the hide until
// a setTimeout or rAF.

const Container = styled.div<ContainerProps>`
    font-size: 12px;
    display: flex;
    position: relative;
    align-items: center;
    border-bottom: 1px solid #e9e9e9;
    padding: 5px 3px 4px 8px;
    -webkit-user-select: none;
    -moz-user-select: none;
    user-select: none;
    cursor: pointer;
    width: 100%;

    transition:
        transform 125ms ease-in-out,
        opacity 60ms linear 30ms;

    opacity: ${(p) => (p.$isHidden ? 1 / 32 : 1)};

    background-color: ${(p) => bgIdleColor(p)};
    &:hover {
        background-color: ${(p) => bgHoverColor(p)};
    }

    font-weight: ${(p) => (p.$isViewing ? 'bold' : 'inherit')};
`;

const IndexLabel = styled.div`
    font-weight: bold;
    min-width: 2.75em;
    padding-right: 0.5em;
    pointer-events: none;
    text-align: right;
`;

interface NameLabelProps {
    $touched?: boolean;
    $error?: boolean;
}

function labelBullet(p: NameLabelProps): string {
    if (p.$error) {
        // Indicate an error with an outer ring. This is not meant to be
        // overt (although it can be combined with a warning emoji). But
        // mostly a signal to those in the know. If a user asks what it means,
        // they're identifying a mild label rendering error.
        return p.$touched ? '"◉"' : '"○"';
    }
    // The common case; solid bullet means unsaved changes
    return p.$touched ? '"•"' : 'none';
}

const NameLabel = styled.div<NameLabelProps>`
    flex-grow: 1;
    pointer-events: none;
    white-space: nowrap;

    &::before {
        padding-right: 0.25em;
        display: ${(p) => (p.$touched || p.$error ? 'inline' : 'none')};
        content: ${(p) => labelBullet(p)};
    }
`;

function evaluateLabel(
    adProperties: DynamicPropertiesManager
): [string, boolean] {
    const prop = adProperties.getChild('label');
    if (!prop) {
        return ['⚠️ No "label" property', false];
    }

    if (prop.type === DynamicPropertyType.LABEL) {
        const labelProp = prop as DynamicPropertyLabel;
        const evaluatedLabel = labelProp.rendered;
        if (evaluatedLabel) return [evaluatedLabel, true];

        const fallbackValue = labelProp.value;
        return [fallbackValue || '⚠️ Empty "label" property', false];
    }

    if (prop.type === DynamicPropertyType.TEXT) {
        const textValue = prop.getString();
        if (textValue) {
            return [textValue, false];
        }
    }

    return [`[${prop.type}] ${prop.getString()}`, false];
}

export interface AdListTableRowProps {
    ad: Ad;
    displace?: number;
    draggable?: boolean;
    isDragSetMember?: boolean;
    onCheck?: AdListTableRowCheckCallback | undefined;
    onClick?: AdListTableRowCallback | undefined;
    onDragStart?: AdListTableRowCallback | undefined;
    onDragEnd?: AdListTableRowCallback | undefined;
}

// No forwardRef: the parent measures rows by querying
// [data-adhub-adlisttablerow] at drag start rather than collecting element
// refs. That matters beyond tidiness - a ref callback declared inline in the
// parent gets a new identity every render, and React's memo bailout requires
// the ref to be unchanged, so passing one here would re-render every row on
// every render despite `observer`.
function AdListTableRow({
    ad,
    displace = 0,
    draggable,
    isDragSetMember = false,
    onCheck,
    onClick,
    onDragStart,
    onDragEnd,
}: AdListTableRowProps) {
    const adProperties = ad.properties;
    const isChanged = adProperties.getChanged();
    const isChecked = ad.selected;
    const isRead = ad.isRead || ad.isViewingComments;

    const [labelString, labelIsOk] = evaluateLabel(adProperties);

    const forceUpdate = useForceUpdate();
    useEffect(
        () => addChangeListener(adProperties, forceUpdate),
        [adProperties, forceUpdate]
    );

    const handleClick = () => {
        onClick?.({ ad });
    };

    const handleCheck = (event: ReactMouseEvent<HTMLElement>) => {
        if (onCheck) {
            event.stopPropagation(); // prevent it from reaching the click handler
            onCheck({ ad, shiftKey: event.shiftKey, newValue: !isChecked });
        }
    };

    const handleDragStart = (event: ReactDragEvent<HTMLElement>) => {
        if (onDragStart) {
            event.dataTransfer.effectAllowed = 'move';

            // Nothing in the app reads this back - the drag set is tracked in
            // component state, not in the dataTransfer. It is set because
            // Firefox will not begin a drag at all unless dragstart writes
            // some data, where Chrome is happy without it.
            //
            // JSON rather than the bare id purely as framing: if anything ever
            // does read it, it will at least parse. text/plain because it is
            // the format every browser accepts; the cost is that dragging a
            // row into an external text field pastes the JSON.
            event.dataTransfer.setData(
                'text/plain',
                JSON.stringify({ id: ad.id })
            );

            onDragStart({ ad });
        }
    };

    const handleDragEnd = (_event: ReactDragEvent<HTMLElement>) => {
        if (onDragEnd) {
            onDragEnd({ ad });
        }
    };

    const transform = `translateY(${displace * 100}%)`;

    return (
        <Container
            $isSelected={ad.selected}
            $isViewing={ad.active}
            $isHidden={isDragSetMember}
            onClick={onClick && handleClick}
            draggable={draggable && !!onDragStart}
            onDragStart={onDragStart && handleDragStart}
            onDragEnd={handleDragEnd}
            style={{ transform }}
            data-adhub-adlisttablerow={ad.id}
        >
            <Checkbox checked={isChecked} onClick={onCheck && handleCheck} />
            <IndexLabel>{Number(ad.data.index) + 1}</IndexLabel>
            <NameLabel $touched={isChanged} $error={!labelIsOk}>
                {labelString}
            </NameLabel>
            {ad.numComments > 0 && (
                <CommentCount isRead={isRead} commentCount={ad.numComments} />
            )}
        </Container>
    );
}

export default observer(AdListTableRow);
